import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Info, X, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export const FloatingFAB = ({ guideContent }) => {
  const [open, setOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const fabRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (fabRef.current && !fabRef.current.contains(e.target) && !showGuide) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [showGuide]);

  return (
    <>
      {/* FAB container */}
      <div ref={fabRef} className="fixed bottom-6 right-6 z-[100] flex flex-col-reverse items-center gap-2" data-testid="floating-fab">
        {/* Main FAB button */}
        <button
          onClick={() => setOpen(prev => !prev)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 cursor-pointer ${
            open
              ? 'bg-dash-ink text-dash-bg rotate-45 scale-95'
              : 'bg-dash-coral text-white hover:shadow-xl hover:scale-105'
          }`}
          data-testid="fab-trigger"
          title="Quick actions"
        >
          {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </button>

        {/* Expanded actions */}
        <div className={`flex flex-col items-center gap-2 transition-all duration-300 ${
          open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}>
          {/* Theme toggle */}
          <button
            onClick={() => { toggleTheme(); setOpen(false); }}
            className="w-10 h-10 rounded-full bg-dash-panel border border-dash-line shadow-lg flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition cursor-pointer group relative"
            data-testid="fab-theme-toggle"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="absolute right-full mr-3 px-2 py-1 rounded bg-dash-panel border border-dash-line text-dash-ink text-xs font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none">
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </span>
          </button>

          {/* Info / Guide button */}
          <button
            onClick={() => { setShowGuide(true); setOpen(false); }}
            className="w-10 h-10 rounded-full bg-dash-panel border border-dash-line shadow-lg flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition cursor-pointer group relative"
            data-testid="fab-info"
            title="How to read this dashboard"
          >
            <Info className="w-4 h-4" />
            <span className="absolute right-full mr-3 px-2 py-1 rounded bg-dash-panel border border-dash-line text-dash-ink text-xs font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none">
              Dashboard guide
            </span>
          </button>
        </div>
      </div>

      {/* Right slide-in guide panel */}
      {showGuide && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[200] transition-opacity"
            onClick={() => setShowGuide(false)}
          />
          {/* Panel */}
          <div
            className="fixed top-0 right-0 h-full w-full max-w-md bg-dash-bg border-l border-dash-line z-[201] overflow-y-auto shadow-2xl animate-in slide-in-from-right"
            data-testid="guide-panel"
          >
            <div className="sticky top-0 bg-dash-bg border-b border-dash-line px-5 py-4 flex items-center justify-between z-10">
              <h2 className="text-sm font-mono font-bold tracking-widest uppercase text-dash-ice">
                How to read this dashboard
              </h2>
              <button
                onClick={() => setShowGuide(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel transition cursor-pointer"
                data-testid="guide-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {guideContent?.sections?.map((s, i) => (
                <div key={i} className="bg-dash-panel border border-dash-line rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-dash-ink mb-1.5">{s.title}</h3>
                  <p className="text-[13px] leading-relaxed text-dash-mut">{s.desc}</p>
                  {s.example && (
                    <p className="text-[12px] leading-relaxed text-dash-ice/70 mt-2 italic">{s.example}</p>
                  )}
                </div>
              ))}
              {guideContent?.lookFor && (
                <div className="bg-dash-panel border border-dash-line rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-dash-ink mb-1.5">What to look for</h3>
                  <p className="text-[13px] leading-relaxed text-dash-mut">{guideContent.lookFor}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
